
import { toast } from "@/components/ui/use-toast";

type HotspotData = {
  x: number;
  y: number;
  timestamp: number;
  page: string;
  deviceType: string;
  clickTarget?: string;
};

type PageViewData = {
  page: string;
  timestamp: number;
  timeSpent: number;
  referrer: string;
  deviceType: string;
  browserInfo: string;
};

type AnalyticsData = {
  pageViews: PageViewData[];
  hotspots: HotspotData[];
  sessionStartTime: number;
  sessionId: string;
};

class AnalyticsService {
  private static instance: AnalyticsService;
  private analyticsData: AnalyticsData;
  private isTracking: boolean = false;
  private sessionTimeoutId: number | null = null;
  private currentPage: string = '';
  private pageEntryTime: number = 0;

  private constructor() {
    const sessionId = this.generateSessionId();
    
    this.analyticsData = {
      pageViews: [],
      hotspots: [],
      sessionStartTime: Date.now(),
      sessionId
    };
  }

  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  private generateSessionId(): string {
    return 'session_' + Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15);
  }
  
  private detectDeviceType(): string {
    const userAgent = navigator.userAgent;
    if (/Mobi|Android|iPhone|iPad|iPod/i.test(userAgent)) {
      return 'mobile';
    }
    if (/iPad|Tablet/i.test(userAgent)) {
      return 'tablet';
    }
    return 'desktop';
  }

  private getBrowserInfo(): string {
    const ua = navigator.userAgent;
    let browser = 'Unknown';
    
    if (ua.indexOf('Chrome') > -1) browser = 'Chrome';
    else if (ua.indexOf('Firefox') > -1) browser = 'Firefox';
    else if (ua.indexOf('Safari') > -1) browser = 'Safari';
    else if (ua.indexOf('Edge') > -1) browser = 'Edge';
    else if (ua.indexOf('MSIE') > -1 || ua.indexOf('Trident/') > -1) browser = 'Internet Explorer';
    
    return browser;
  }

  public startTracking(): void {
    if (this.isTracking) return;
    
    this.isTracking = true;
    this.currentPage = window.location.pathname;
    this.pageEntryTime = Date.now();
    
    // Track clicks for hotspot detection
    document.addEventListener('click', this.trackClick);
    
    // Track page changes
    this.handlePageChange();
    
    // Set a session timeout (30 minutes)
    this.sessionTimeoutId = window.setTimeout(() => {
      this.stopTracking();
    }, 30 * 60 * 1000);
    
    console.log('Analytics tracking started');
  }

  public stopTracking(): void {
    if (!this.isTracking) return;
    
    this.isTracking = false;
    document.removeEventListener('click', this.trackClick);
    
    if (this.sessionTimeoutId) {
      window.clearTimeout(this.sessionTimeoutId);
      this.sessionTimeoutId = null;
    }
    
    // Record the final page view before stopping
    this.recordPageExit();
    
    console.log('Analytics tracking stopped');
  }

  private handlePageChange = (): void => {
    // For single-page applications, we need to listen for route changes
    const originalPushState = window.history.pushState;
    window.history.pushState = function() {
      originalPushState.apply(this, arguments);
      AnalyticsService.getInstance().recordPageChange();
    };
    
    window.addEventListener('popstate', () => {
      this.recordPageChange();
    });
  }

  private recordPageChange = (): void => {
    // Record the previous page first
    this.recordPageExit();
    
    // Start tracking the new page
    this.currentPage = window.location.pathname;
    this.pageEntryTime = Date.now();
  }

  private recordPageExit = (): void => {
    if (!this.isTracking || !this.pageEntryTime) return;
    
    const now = Date.now();
    const timeSpent = now - this.pageEntryTime;
    
    this.analyticsData.pageViews.push({
      page: this.currentPage,
      timestamp: this.pageEntryTime,
      timeSpent,
      referrer: document.referrer,
      deviceType: this.detectDeviceType(),
      browserInfo: this.getBrowserInfo()
    });
  }

  private trackClick = (event: MouseEvent): void => {
    if (!this.isTracking) return;
    
    const target = event.target as HTMLElement;
    let clickTarget = target.tagName;
    
    // Try to get more specific info about what was clicked
    if (target.id) {
      clickTarget += `#${target.id}`;
    } else if (target.className) {
      clickTarget += `.${target.className.split(' ')[0]}`;
    }
    
    // Record the click as a hotspot
    this.analyticsData.hotspots.push({
      x: event.clientX,
      y: event.clientY,
      timestamp: Date.now(),
      page: this.currentPage,
      deviceType: this.detectDeviceType(),
      clickTarget
    });
  }

  public getHotspotData(): HotspotData[] {
    return [...this.analyticsData.hotspots];
  }

  public getPageViewData(): PageViewData[] {
    // Make sure we have the latest data
    this.recordPageExit();
    this.pageEntryTime = Date.now();
    
    return [...this.analyticsData.pageViews];
  }

  public exportToExcel(): void {
    // In a real app, this would create an Excel file
    // For now, we'll just simulate this with a JSON file
    const data = JSON.stringify(this.analyticsData, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `analytics_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Analytics Exported",
      description: "Your analytics data has been exported successfully.",
    });
  }
}

export default AnalyticsService;
